import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from '../Brand/Icon';
import styles from './Ways.module.css';

// The pictures shared by the landing page's "ways to send" and the send
// wizard (docs/adr/0014): a You → server → Them route with a lock on every
// leg, and a list of facts each led by an icon.

export type Way = 'drop' | 'live' | 'relay';

/** The little route drawing. Decorative: the facts next to it say the same in words. */
export function RouteDiagram({ way, muted = false }: { way: Way; muted?: boolean }) {
  const { t } = useTranslation();
  const node = (icon: IconName, label: string, server = false) => (
    <div className={styles.node}>
      <span className={server ? styles.nodeServer : styles.nodeEnd}>
        <Icon name={icon} />
      </span>
      <span className={styles.nodeLabel}>{label}</span>
    </div>
  );
  const leg = (label: string, later = false) => (
    <div className={styles.leg}>
      <span className={later ? styles.lineLater : styles.line}>
        <span className={styles.lineLock}>
          <Icon name="lock" size={12} strokeWidth={2.4} />
        </span>
      </span>
      <span className={styles.legLabel}>{label}</span>
    </div>
  );
  return (
    <div className={`${styles.route} ${muted ? styles.muted : ''}`} aria-hidden={muted || undefined}>
      <p className={styles.routeNote}>
        <Icon name="lock" size={14} strokeWidth={2.2} />
        {t('route.encrypted')}
      </p>
      <div className={styles.routeRow} aria-hidden="true">
        {node('laptop', t('route.you'))}
        {way === 'drop' && (
          <>
            {leg(t('route.now'))}
            {node('clock', t('route.holds'), true)}
            {leg(t('route.later'), true)}
          </>
        )}
        {way === 'live' && leg(t('route.direct'))}
        {way === 'relay' && (
          <>
            {leg(t('route.in'))}
            {node('arrow', t('route.passes'), true)}
            {leg(t('route.out'))}
          </>
        )}
        {node('person', t('route.them'))}
      </div>
    </div>
  );
}

export interface Fact {
  icon: IconName;
  strong: string;
  rest: string;
}

/** Facts led by an icon, with the part that matters in bold. */
export function FactList({ facts, muted = false, id }: { facts: Fact[]; muted?: boolean; id?: string }) {
  return (
    <ul className={`${styles.facts} ${muted ? styles.factsMuted : ''}`} id={id}>
      {facts.map((fact) => (
        <li key={fact.strong}>
          <Icon name={fact.icon} size={18} strokeWidth={2} className={styles.factIcon} />
          <span>
            <strong>{fact.strong}</strong>
            {fact.rest}
          </span>
        </li>
      ))}
    </ul>
  );
}
